import React, { useState } from 'react'

import type { IButtonProps } from './types'

import * as Comp from './button.styles'

import { ButtonProvider } from './contexts/button-context'

import { SCOPE_WARNING_MESSAGE } from './utils/const'

import { Refresh } from '~/components/refresh'
import { ButtonIcon, ButtonLabel } from './components'

import type { GestureResponderEvent } from 'react-native'
import { isDEV, logger } from '~/utils'

const ButtonRoot = React.forwardRef<
  React.ElementRef<typeof Comp.Root>,
  IButtonProps
>(
  (
    {
      children,
      variant = 'brand-primary',
      size = 'md',
      full,
      disabled,
      loading,
      onPressIn,
      onPressOut,
      ...props
    },
    ref
  ) => {
    const [isPressed, setIsPressed] = useState(false)

    const renderInternalComponents = React.Children.map(children, child => {
      if (!React.isValidElement(child)) return

      if (child.type === ButtonLabel || child.type === ButtonIcon) {
        return child
      }

      if (isDEV()) {
        logger.warning(SCOPE_WARNING_MESSAGE)
        return
      }
    })

    const isDisabled = !!disabled
    const isLoading = !!loading

    const refreshColor = variant === 'brand-primary' ? 'white' : 'neutral'

    const accessibilityProperties = {
      role: 'button',
      'aria-disabled': isDisabled,
      accessible: true,
    } as const

    const isOnlyIcon = renderInternalComponents
      ? renderInternalComponents?.length > 1
        ? false
        : renderInternalComponents[0].type === ButtonIcon
      : false

    const componentProperties = {
      size,
      variant,
      full,
      disabled: isDisabled || isLoading,
      hasPressed: isPressed,
      hasLoading: isLoading,
      hasDisabled: isDisabled && !isLoading,
      hasFull: full,
      hasOnlyIcon: isOnlyIcon,
    } as const

    function handlePressIn(event: GestureResponderEvent) {
      setIsPressed(true)
      onPressIn?.(event)
    }

    function handlePressOut(event: GestureResponderEvent) {
      setIsPressed(false)
      onPressOut?.(event)
    }

    return (
      <ButtonProvider {...componentProperties}>
        <Comp.Root
          ref={ref}
          {...props}
          {...componentProperties}
          {...accessibilityProperties}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
        >
          {isLoading ? (
            <Refresh size="sm" color={refreshColor} />
          ) : (
            renderInternalComponents?.slice(0, 3)
          )}
        </Comp.Root>
      </ButtonProvider>
    )
  }
)

ButtonRoot.displayName = 'Button'

export const Button = {
  Root: ButtonRoot,
  Label: ButtonLabel,
  Icon: ButtonIcon,
}

export type { IButtonProps }
